/**
 * Intégration spatiale du profil de vitesse — ce que le site utilise.
 * Port fidèle de speed_profile() dans tools/model_reference.py :
 *   1. passe avant   v² += 2·a(v,x)·Δx, plafonnée par vmax(x), départ à 0 à chaque arrêt ;
 *   2. passe arrière courbe de freinage remontée depuis chaque arrêt et chaque chute de vmax ;
 *   3. v(x) = min(avant, arrière), t = Σ Δx / v̄ entre le premier et le dernier arrêt ;
 *   4. + (tarr + (a0+d)/j) par arrêt intermédiaire.
 */
import { ETA, G, JERK, davisFor, type RollingStock } from "./rollingstock";

export interface ProfileOptions {
  /** plafond de vitesse par segment (m/s), longueur n ; absent = vmax du matériel */
  vmaxProfile?: ArrayLike<number> | null;
  /** temps d'arrêt en gare, s */
  tarr?: number;
  /** résistance à l'avancement (Davis) */
  davis?: boolean;
}

export interface ProfileResult {
  /** temps total premier → dernier arrêt, arrêts intermédiaires compris, s */
  T: number;
  /** temps de marche pur, s */
  tRun: number;
  /** temps immobilisé en gare + jerk, s */
  tStops: number;
  /** vitesse en chaque point de la grille (n+1 valeurs), m/s */
  v: Float64Array;
  /** temps cumulé de marche en chaque point de la grille (n+1), hors arrêts, s */
  tCum: Float64Array;
  /** arrêts triés, dédoublonnés, bornés à n */
  stops: number[];
}

/** Arrêts triés, dédoublonnés, bornés à [0, n]. */
export function normalizeStops(stops: ArrayLike<number>, n: number): number[] {
  const set = new Set<number>();
  for (let k = 0; k < stops.length; k++) set.add(Math.min(Math.max(0, stops[k] | 0), n));
  return [...set].sort((a, b) => a - b);
}

/**
 * @param stopsIdx indices (dans la grille de pas dx) des arrêts, terminus inclus
 * @param grade    pente algébrique par segment, longueur n
 * @param dx       pas d'espace, m
 */
export function speedProfile(
  stopsIdx: ArrayLike<number>,
  grade: ArrayLike<number>,
  dx: number,
  stock: RollingStock,
  opts: ProfileOptions = {},
): ProfileResult {
  const { tarr = 30.0, davis = true } = opts;
  const { mass_kg: m, power_w: P, a0, d, vmax_ms: vmax } = stock;
  const [A, B, C] = davis ? davisFor(stock) : [0, 0, 0];
  const n = grade.length;
  const cap = opts.vmaxProfile ?? null;
  const capAt = (x: number) => (cap ? cap[x] : vmax);
  const stops = normalizeStops(stopsIdx, n);
  const stopSet = new Set(stops);

  const forward = new Float64Array(n + 1);
  for (let x = 0; x < n; x++) {
    if (stopSet.has(x)) forward[x] = 0.0; // redémarrage à l'arrêt depuis chaque gare
    const v = Math.max(forward[x], 0.5);
    const a = (Math.min(m * a0, (ETA * P) / v) - (A + B * v + C * v * v)) / m - G * grade[x];
    forward[x + 1] = Math.min(capAt(x), Math.sqrt(Math.max(0.0, forward[x] ** 2 + 2 * a * dx)));
  }

  const backward = new Float64Array(n + 1);
  for (let x = 0; x <= n; x++) backward[x] = Math.min(capAt(Math.min(x, n - 1)), vmax);
  for (const s of stops) backward[Math.min(s, n)] = 0.0;
  for (let x = n - 1; x >= 0; x--) {
    const db = Math.max(d + G * grade[x], 0.2);
    backward[x] = Math.min(backward[x], Math.sqrt(Math.max(0.0, backward[x + 1] ** 2 + 2 * db * dx)));
  }

  const v = new Float64Array(n + 1);
  for (let x = 0; x <= n; x++) v[x] = Math.min(forward[x], backward[x]);

  const tCum = new Float64Array(n + 1);
  for (let x = 0; x < n; x++) tCum[x + 1] = tCum[x] + dx / Math.max((v[x] + v[x + 1]) / 2, 0.5);

  // le temps ne court qu'entre le premier et le dernier arrêt
  const first = stops.length ? stops[0] : 0;
  const last = stops.length ? stops[stops.length - 1] : n;
  let tRun = 0.0;
  for (let x = first; x < last; x++) tRun += dx / Math.max((v[x] + v[x + 1]) / 2, 0.5);
  const tStops = Math.max(0, stops.length - 2) * (tarr + (a0 + d) / JERK);
  return { T: tRun + tStops, tRun, tStops, v, tCum, stops };
}
